import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import { ReactComponent as Logo } from "../../../../.MODULS/UCAT_EXAM_MODUL/Assests/download-min.svg";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { listInstructionExamID } from "../../../../Redux/Slices/Exam/ListinstructionExamIDSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { UcatLayout, UcatModal } from "../../Components/Shared";
import { ReloadWarningModal, ScrollWarningModal } from "../../Components/Practice_Test/Exam_component";

export const UCAT_WELCOME_DATA = {
  examBrand: {
    short: "UCAT",
    full: "University Clinical Aptitude Test",
  },
};

const UcatAgreement: React.FC = () => {
  console.info("does it work?", "yaah");

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const exam_id = location.state?.exam_id; // Fix: access state properly

  const [showScrollWarning, setShowScrollWarning] = useState(false);
  const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [showReloadWarning, setShowReloadWarning] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Pearson-style browser back-button trap
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setShowReloadWarning(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSubmittingRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "F5" || ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R"))) {
        e.preventDefault();
        setShowReloadWarning(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const blocker = useBlocker(
    ({ historyAction }) => {
      if (isSubmittingRef.current) return false;
      return true; // Block both back button (POP) and other nav (PUSH)
    }
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowReloadWarning(true);
    } else {
      setShowReloadWarning(false);
    }
  }, [blocker.state]);

  const handleReloadConfirm = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      window.location.reload();
    }
    setShowReloadWarning(false);
  };

  const handleReloadCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setShowReloadWarning(false);
  };

  // Get data from Redux
  const listInstructionExamSlice = useAppSelector(
    (state) => state.ListInstructionExamSlice,
  );
  const examData = listInstructionExamSlice?.data;
  const isLoading = listInstructionExamSlice?.isLoading;

  const scrollStatusRef = useRef(scrollStatus);

  // Update ref when scrollStatus changes
  useEffect(() => {
    scrollStatusRef.current = scrollStatus;
  }, [scrollStatus]);

  const checkScrollStatus = useCallback(() => {
    const buffer = 10;
    let newRight = scrollStatusRef.current.right;

    if (!newRight && scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (
        scrollHeight <= clientHeight ||
        scrollTop + clientHeight >= scrollHeight - buffer
      ) {
        newRight = true;
      }
    }

    if (newRight !== scrollStatusRef.current.right) {
      setScrollStatus({ left: true, right: newRight });
    }
  }, []); // No dependencies since we use ref

  const handleScroll = useCallback(() => {
    checkScrollStatus();
  }, [checkScrollStatus]);

  // Check initial scroll status on mount
  useEffect(() => {
    const checkInitial = () => {
      let r = true;
      if (scrollRef.current && scrollRef.current.scrollHeight > scrollRef.current.clientHeight + 5) {
        r = false;
      }
      return { left: true, right: r };
    };

    // Reset scroll position only on initial mount
    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    const initial = checkInitial();
    setScrollStatus(initial);

    // Re-check after a brief delay for content layout
    const timeoutId = setTimeout(() => {
      setScrollStatus(checkInitial());
    }, 300);

    const handleResize = () => {
      if (scrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const buffer = 10;
        const newRight = scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer;

        if (newRight !== scrollStatusRef.current.right) {
          setScrollStatus({ left: true, right: newRight });
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array - only run once on mount

  // Debug logging
  console.log("Redux state:", {
    listInstructionExamSlice,
    examData,
    isLoading,
    exam_id,
  });

  // Fetch exam instruction data on mount
  useEffect(() => {
    if (exam_id) {
      // dispatch(listInstructionExamID({ exam_id: exam_id }));
    } else {
      console.warn("No exam_id provided in location.state");
    }
  }, [dispatch, exam_id]);

  const handleNext = () => {
    if (!scrollStatus.left || !scrollStatus.right) {
      setShowScrollWarning(true);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    navigate("/ucat_exam_rules", {
      state: {
        exam_id: exam_id,
      },
    });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Only handle Alt key combinations
    if (e.altKey) {
      const key = e.key.toLowerCase();

      // Prevent default browser behavior
      e.preventDefault();

      if (showScrollWarning) {
        // Warning context
        if (key === "o") {
          setShowScrollWarning(false);
        }
      } else {
        // Main screen context
        switch (key) {
          case "e":
            // End exam action
            navigate("/ucat_test");
            break;
          case "n":
            handleNext();
            break;
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, showScrollWarning, handleNext]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <CircularProgress />
      </div>
    );
  }

  // Use fallback data if examData is not available
  const headerTitle = examData?.data?.exam_name || "UCAT Practice";
  const subTitle = examData?.data?.exam_name || "";
  return (
    <UcatLayout
      headerTitle={headerTitle}
      // headerSubtitle={subTitle}
      showToolbar={true}
      footerProps={{
        onEndExam: () => navigate("/ucat_test"),
        onNext: handleNext,
        leftButtonText: "End Exam",
        rightButtonText: "Next",
      }}
    >
      {/* ================= CONTENT ================= */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto ucat-scrollbar min-h-0"
      >
        <div className="max-w-[100%] mx-auto px-4 py-4">
          {/* Logo */}
          <div className="flex justify-end mb-[2px]">
            <Logo className="w-[456px] h-auto" />
          </div>

          {/* Text Content */}
          <div className="space-y-4 text-[14px] leading-[17px] text-black max-w-full">
            <div>
              <p style={{ fontSize: "15px" }}>
                Welcome to the{" "}
                <b style={{ fontSize: "inherit", fontWeight: "700" }}>UCAT </b>{" "}
                exam.
                <br /> <br />
                Please verify your name and examination title. If either is
                incorrect, raise your hand for the invigilator (if you are in a
                test centre) or make contact with the proctor (if testing online).
                <br /> <br />
                If you choose to proceed you acknowledge that you will be
                monitored at all times and both audio and video may be recorded.
                If you have questions about this policy, raise your hand for the
                invigilator (if you are in a test centre) or make contact with the
                proctor (if testing online).
                <br /> <br />
                Non-disclosure agreement and general terms of use for exams
                developed for the UCAT Consortium:
                <br /> <br />
                <ul
                  style={{
                    listStyleType: "disc",
                    paddingLeft: "1.5rem",
                    marginLeft: "4rem",
                    fontSize: "inherit",
                  }}
                >
                  <li style={{ fontSize: "inherit" }}>
                    {" "}
                    The exam is made available to you as an examinee solely for
                    the purpose of being assessed in this exam. You are expressly
                    prohibited from disclosing, publishing, reproducing or
                    transmitting this exam, in whole or in part, in any form or by
                    any means including visual, aural, verbal, written, electronic
                    or mechanical means, for any purpose, without the prior
                    express written permission of the UCAT Consortium.{" "}
                  </li>

                  <li style={{ fontSize: "inherit" }}>
                    {" "}
                    Your personal data and test data will be stored in Pearson VUE
                    and UCAT databases.{" "}
                  </li>

                  <li style={{ fontSize: "inherit" }}>
                    {" "}
                    All examination contents are the published, confidential and
                    propriety materials of UCAT.{" "}
                  </li>

                  <li style={{ fontSize: "inherit" }}>
                    {" "}
                    All copyright is owned by the UCAT Consortium.{" "}
                  </li>
                </ul>
                <br />
                Click the{" "}
                <b style={{ fontSize: "inherit", fontWeight: "700" }}>
                  Next (N){" "}
                </b>{" "}
                button when you are ready to begin the exam.
              </p>
            </div>

            {/* {UCAT_WELCOME_DATA.content.paragraphs.map((text, index) => (
            <p key={index} className="text-[15px] font-[400]">{text}</p>
            ))} */}
          </div>
        </div>
      </div>

      {/* ================= CONFIRMATION DIALOG ================= */}


      {/* ================= SCROLL WARNING MODAL ================= */}
      <ScrollWarningModal
        isOpen={showScrollWarning}
        onClose={() => setShowScrollWarning(false)}
      />
      <ReloadWarningModal
        isOpen={showReloadWarning}
        onClose={handleReloadCancel}
        onConfirm={handleReloadConfirm}
      />
    </UcatLayout>
  );
};

export default UcatAgreement;
